import {
  ConsultantGdprConsentFragment,
  GDPRCommunicationChannel,
  GDPRConsentFragment,
  GDPRConsentType,
  GDPROptionType,
  useGetConsultantGdprQuery,
  useUpdateConsultantGdprMutation
} from "@graphql";
import { useLocale, useSnackbars } from "@hooks";
import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { ThemeType } from "@theme";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  Typography
} from "@material-ui/core";
import { EditIcon, Modal } from "@bonliva-crm/ui";
import moment from "moment";
import { isEqual } from "lodash";
import { normalizeConsent } from "./utils";

type Props = {
  consultantId: string;
};

export function ConsultantGdpr({ consultantId }: Props) {
  const { showSuccess, showError } = useSnackbars();
  const { locale } = useLocale();
  const classes = useStyles();

  const { data, loading } = useGetConsultantGdprQuery({
    variables: {
      consultantId
    },
    fetchPolicy: "cache-first",
    onError: (error) => {
      showError(locale.ErrorOccurred);
      console.error(error.message);
    }
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [initialConsent, setInitialConsent] =
    useState<ConsultantGdprConsentFragment | null>(null);
  const [selectedConsent, setSelectedConsent] =
    useState<ConsultantGdprConsentFragment | null>(null);

  const [mutate, { loading: saving }] = useUpdateConsultantGdprMutation({
    variables: {
      id: consultantId,
      input: {
        type: selectedConsent?.type,
        granted: selectedConsent?.granted,
        communicationChannel: selectedConsent?.communicationChannel
      }
    },
    onCompleted: () => {
      showSuccess(locale.Saved);
    },
    onError: (error) => {
      showError(locale.ErrorOccurred);
      console.error(error.message);
    }
  });

  if (loading) {
    return null;
  }

  const onConsentEdit = (
    initial: ConsultantGdprConsentFragment,
    selected: ConsultantGdprConsentFragment
  ) => {
    setInitialConsent(initial);
    setSelectedConsent(selected);
    setModalOpen(true);
  };

  const consents = data.consultantById.GDPRConsentRecords;
  const integrityPolicyConsent = consents.find(
    (consent) => consent.type === GDPRConsentType.IntegrationPolicy
  );
  const marketingConsent = consents.find(
    (consent) => consent.type === GDPRConsentType.Marketing
  );
  const mailingEmailConsent = consents.find(
    (consent) => consent.type === GDPRConsentType.JobMailingEmail
  );
  const mailingSmsConsent = consents.find(
    (consent) => consent.type === GDPRConsentType.JobMailingSms
  );
  const mailingCampaignConsent = consents.find(
    (consent) => consent.type === GDPRConsentType.JobMailingCampaignEmail
  );

  const modalContent = (consent: ConsultantGdprConsentFragment) => {
    const isGrantedOptions = Object.keys(locale.consultantGDPROption).filter(
      (option) => option !== GDPROptionType.NotSpecified
    );
    const isGrantedDefaultValue = consent.granted ? "Yes" : "No";

    return (
      <Box className={classes.modalContainer}>
        <FormControl>
          <FormLabel>
            <Typography variant="h5">
              {locale.GrantedConsent(locale.gdprConsentType[consent.type])}
            </Typography>
          </FormLabel>
          <RadioGroup>
            {isGrantedOptions.map((option) => (
              <FormControlLabel
                value={option}
                control={<Radio />}
                label={locale.consultantGDPROption[option]}
                checked={option === isGrantedDefaultValue}
                onChange={() => {
                  setSelectedConsent({
                    ...selectedConsent,
                    granted: option === "Yes"
                  });
                }}
              />
            ))}
          </RadioGroup>
        </FormControl>

        <FormControl>
          <FormLabel>
            <Typography variant="h5">{locale.InformationCameVia}</Typography>
          </FormLabel>
          <RadioGroup>
            {Object.keys(locale.consultantGDPRChannelShort).map((option) => (
              <FormControlLabel
                value={option}
                control={<Radio />}
                label={locale.consultantGDPRChannel[option]}
                checked={option === consent.communicationChannel}
                onChange={() => {
                  setSelectedConsent({
                    ...selectedConsent,
                    communicationChannel: option as GDPRCommunicationChannel
                  });
                }}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </Box>
    );
  };

  const isDirty = () => {
    const channels = Object.keys(locale.consultantGDPRChannelShort);

    return (
      !isEqual(initialConsent, selectedConsent) ||
      !channels.includes(selectedConsent.communicationChannel)
    );
  };

  const modalTitle = selectedConsent
    ? locale.EditGdprConsent(locale.gdprConsentType[selectedConsent.type])
    : null;

  return (
    <Box className={classes.root}>
      <Row
        label={locale.ProcessingDataLegalBasis}
        value={locale.legalBasisType[data.consultantById.LegalBasis]}
      />
      <Divider className={classes.divider} />
      <ConsentItem
        label={locale.ApprovedPrivacyPolicy}
        consent={integrityPolicyConsent}
        type={GDPRConsentType.IntegrationPolicy}
        isEditable={false}
      />
      <Divider className={classes.divider} />
      <ConsentItem
        label={locale.ApprovedMarketing}
        consent={marketingConsent}
        type={GDPRConsentType.Marketing}
        isEditable={true}
        onConsentEdit={onConsentEdit}
      />
      <Divider className={classes.divider} />
      <ConsentItem
        label={locale.ApprovedMailingVia("Email")}
        consent={mailingEmailConsent}
        type={GDPRConsentType.JobMailingEmail}
        isEditable={true}
        onConsentEdit={onConsentEdit}
      />
      <Divider className={classes.divider} />
      <ConsentItem
        label={locale.ApprovedMailingVia("SMS")}
        consent={mailingSmsConsent}
        type={GDPRConsentType.JobMailingSms}
        isEditable={true}
        onConsentEdit={onConsentEdit}
      />
      <Divider className={classes.divider} />
      <ConsentItem
        label={locale.ApprovedCampaignMailingVia("Email")}
        consent={mailingCampaignConsent}
        type={GDPRConsentType.JobMailingCampaignEmail}
        isEditable={true}
        onConsentEdit={onConsentEdit}
      />
      <Divider className={classes.divider} />
      <Row
        label={locale.SignedContract}
        value={data.consultantById.ContractSigned ? locale.Signed : locale.NotSigned}
      />

      {modalOpen && (
        <Modal
          open={modalOpen}
          setOpen={(open) => {
            if (saving) return;
            setModalOpen(open);
          }}
          modalContent={
            <Box className={classes.modalContentWrapper}>
              {modalContent(selectedConsent)}
              {saving && (
                <Box className={classes.savingOverlay}>
                  <CircularProgress size={32} />
                </Box>
              )}
            </Box>
          }
          label={modalTitle}
          rightCta={
            <Box className={classes.ctaButtons}>
              <Button
                variant="outlined"
                color="primary"
                disabled={saving}
                onClick={() => setModalOpen(false)}
              >
                {locale.Cancel}
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={!isDirty() || saving}
                onClick={async () => {
                  await mutate();
                  setModalOpen(false);
                }}
              >
                {locale.Save}
              </Button>
            </Box>
          }
          style={{ width: "50%", left: "25%" }}
          dimensions={{ minHeight: "auto", minWidth: "550px" }}
        />
      )}
    </Box>
  );
}

type RowProps = {
  label: string;
  value: string;
};

function Row({ label, value }: RowProps) {
  const classes = useStyles();

  return (
    <Box>
      <Typography variant="h6">{label}</Typography>
      <Box className={classes.rowValue}>{value || "-"}</Box>
    </Box>
  );
}

type ConsentItemProps = {
  label: string;
  type: GDPRConsentType;
  consent: ConsultantGdprConsentFragment;
  isEditable: boolean;
  onConsentEdit?: (
    initial: ConsultantGdprConsentFragment,
    selected: ConsultantGdprConsentFragment
  ) => void;
};

function ConsentItem({
  label,
  consent,
  type,
  isEditable,
  onConsentEdit
}: ConsentItemProps) {
  const { locale } = useLocale();
  const classes = useStyles();

  const formatCommittedBy = (name: string, date: Date) => {
    return `${name} • ${moment(date).format("YYYY-MM-DD")}`;
  };

  const committedBy = (consent: ConsultantGdprConsentFragment) => {
    if (consent.granted) {
      return formatCommittedBy(consent.grantedBy.Name, consent.dateGranted);
    }

    return formatCommittedBy(consent.revokedBy.Name, consent.dateRevoked);
  };

  const isGrantedValue = (consent?: GDPRConsentFragment) => {
    if (!consent) {
      return locale.notSpecified;
    }

    return consent.granted ? locale.Granted : locale.NotGranted;
  };

  const grantedByValue = consent ? committedBy(consent) : null;

  const communicationChannelValue = consent
    ? locale.consultantGDPRChannel[consent.communicationChannel]
    : null;

  const normalizedConsent = normalizeConsent(type, consent);

  return (
    <Box className={classes.consentContainer}>
      <Box className={classes.consentBox}>
        <Row label={label} value={isGrantedValue(consent)} />
        <Row label={locale.EnteredBy} value={grantedByValue} />
        <Row
          label={isEditable ? locale.InformationCameVia : locale.AcceptedVia}
          value={communicationChannelValue}
        />
      </Box>
      {isEditable && onConsentEdit && (
        <IconButton
          onClick={() => onConsentEdit(consent, normalizedConsent)}
          style={{
            marginLeft: "auto",
            marginBottom: "auto",
            alignItems: "start",
            padding: "6px"
          }}
        >
          <EditIcon />
        </IconButton>
      )}
    </Box>
  );
}

const useStyles = makeStyles((theme: ThemeType) => {
  return {
    root: {
      padding: theme.spacing(2),
      backgroundColor: theme.palette.common.white,
      maxWidth: 500
    },
    rowValue: {
      marginTop: 6
    },
    divider: {
      margin: "1rem 0"
    },
    consentContainer: {
      display: "flex",
      flexDirection: "row",
      gap: 0
    },
    consentBox: {
      display: "flex",
      flexDirection: "column",
      gap: "1rem"
    },
    modalContainer: {
      display: "flex",
      flexDirection: "column",
      gap: "2rem"
    },
    modalContentWrapper: {
      position: "relative"
    },
    savingOverlay: {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "rgba(255, 255, 255, 0.6)",
      zIndex: 1
    },
    ctaButtons: {
      display: "flex",
      justifyContent: "flex-end",
      gap: "1rem",
      marginLeft: "auto"
    }
  };
});
